import React, {FormEvent, memo, ReactNode, useCallback, useEffect, useRef, useState} from "react"
import {useDebounce} from "../../../hooks/debounce"
import {onClickHandler} from "../../../utils/onclick"
import {RgbColor} from "../../ColorPicker/types"
import {Widget} from "../../Widget"
import {RgbColorPicker} from "react-colorful"
import "./style.scss"

type Props = {
    color: RgbColor
    name: string
    value: number
    onChange: (value: number) => void
    onChangeColor: (value: RgbColor) => void
    activeImage: string
    passiveImage: string
    height?: string
    width?: string
    children?: ReactNode
}

export const RGBColorPickerComponent = memo(function RangeComponent(props: Props) {
    const {name, value: valueProp, onChange: onChangeProp, children, activeImage, passiveImage} = props
    const [tempValue, setTempValue] = useState(0)
    const [value, setValue] = useState(valueProp)
    const timeoutRef = useRef<number>()

    const onChangeCallback = useDebounce(onChangeProp, 600)

    const click = useCallback(() => {
        let newValue
        if (value > 0) {
            setTempValue(value)
            newValue = 0
        } else if (tempValue > 0) {
            newValue = tempValue
        } else {
            newValue = 100
        }

        setValue(newValue)
        onChangeCallback(newValue)
    }, [tempValue, value, onChangeCallback])

    const onChange = useCallback(
        (e: FormEvent<HTMLInputElement>) => {
            // @ts-ignore
            const newValue = e.target.valueAsNumber
            if (newValue !== value) {
                setValue(newValue)
                onChangeCallback(newValue)
            }
        },
        [onChangeCallback, value]
    )

    useEffect(() => {
        setValue(valueProp)
    }, [valueProp])

    useEffect(() => {
        // @ts-ignore
        timeoutRef.current = setTimeout(() => {
            if (value !== valueProp) {
                setValue(valueProp)
            }
        }, 2000)

        return () => clearTimeout(timeoutRef.current)
    }, [value, valueProp])

    return (
        <Widget>
            <div className="position-relative">
                <div className="color-component-wrapper">
                    <div className="range">
                        <div className="range-image-wrapper">
                            <div className="range-image">
                                <div {...onClickHandler(click)}>
                                    <img src={value === 0 ? passiveImage : activeImage} alt={name} />
                                </div>
                            </div>
                        </div>
                        <div className="top-range-wrapper">
                            <div className="range-title">
                                <span>{name}</span>
                            </div>
                            <div className="range-control-value">
                                <span>
                                    <span>{value}%</span>
                                </span>
                            </div>
                            <div className="range-slide-control">
                                <input
                                    type="range"
                                    name="points"
                                    value={value}
                                    min={0}
                                    max={100}
                                    step={10}
                                    onChange={onChange}
                                    onInput={onChange}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="color-picker-wrapper">
                        <div className="response-color-picker">
                            <div className="custom-color-picker">
                                <span
                                    className="red"
                                    {...onClickHandler(() => props.onChangeColor({r: 255, g: 0, b: 0}))}
                                />
                                <span
                                    className="orange"
                                    {...onClickHandler(() => props.onChangeColor({r: 99, g: 29, b: 0}))}
                                />
                                <span
                                    className="yellow"
                                    {...onClickHandler(() => props.onChangeColor({r: 255, g: 255, b: 0}))}
                                />
                                <span
                                    className="green"
                                    {...onClickHandler(() => props.onChangeColor({r: 0, g: 255, b: 0}))}
                                />
                                <span
                                    className="aqua"
                                    {...onClickHandler(() => props.onChangeColor({r: 0, g: 255, b: 255}))}
                                />
                                <span
                                    className="blue"
                                    {...onClickHandler(() => props.onChangeColor({r: 0, g: 0, b: 255}))}
                                />
                                <span
                                    className="purple"
                                    {...onClickHandler(() => props.onChangeColor({r: 44, g: 0, b: 99}))}
                                />
                                <span
                                    className="pink"
                                    {...onClickHandler(() => props.onChangeColor({r: 255, g: 0, b: 255}))}
                                />
                                <span
                                    className="white"
                                    {...onClickHandler(() => props.onChangeColor({r: 99, g: 99, b: 99}))}
                                />
                            </div>
                            <RgbColorPicker color={props.color} onChange={props.onChangeColor} />
                        </div>
                    </div>
                </div>
                {children}
            </div>
        </Widget>
    )
})
